// Comments on a register item — threads like Smartsheet conversations: a comment starts a thread, replies hang off it.
// @mention people, attach files, edit or delete your own. Internal (SWI only) vs shared with outside users.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../lib/app';
import { supabase } from '../lib/supabase';
import { useRows } from '../lib/useRows';
import { write } from '../lib/api';
import { uploadFile } from '../lib/files';
import { fmtDateTime } from '../lib/format';
import { initials } from '../lib/presence';
import { Button, Empty, errText, useToast } from './kit';
import { useViewer } from './Viewer';

export type Comment = {
  id: string;
  parent_id: string | null;
  body: string;
  mentions: string[];
  mention_emails: string[];
  author_id: string | null;
  author_name: string | null;
  author_email: string | null;
  posted_at: string;
  edited_at: string | null;
  visibility: 'internal' | 'shared';
  ss_comment_id: number | null;
  updated_at: string;
  deleted_at: string | null;
};
export type ItemFile = { id: string; original_name: string; comment_id: string | null; kind: string; mime?: string | null; deleted_at: string | null };
type Person = { id: string; full_name: string | null; email: string | null };

const INTERNAL_ROLES = new Set(['super_admin', 'pm', 'field', 'swi_finance']);
const nameOf = (p: Person) => p.full_name || p.email || 'Someone';

/** Comment text with @mentions highlighted. */
function Body({ text, people }: { text: string; people: Person[] }) {
  const names = people.map(nameOf).filter((n) => n.length > 1).sort((a, b) => b.length - a.length);
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`(@(?:${[...names.map(esc), '[\\w.+-]+@[\\w-]+(?:\\.[\\w-]+)+'].join('|')}))`, 'g');
  return (
    <p className="cmt-text">
      {text.split(re).map((part, i) =>
        i % 2 === 1 ? (
          <span key={i} className="mention">
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </p>
  );
}

function Composer({
  people,
  internal,
  placeholder,
  initial = '',
  onSend,
  onCancel,
  allowFiles = true,
  allowVisibility = true,
  sendLabel = 'Post',
}: {
  people: Person[];
  internal: boolean;
  placeholder: string;
  initial?: string;
  onSend: (body: string, mentions: string[], visibility: 'internal' | 'shared', files: File[]) => Promise<void>;
  onCancel?: () => void;
  allowFiles?: boolean;
  allowVisibility?: boolean;
  sendLabel?: string;
}) {
  const [text, setText] = useState(initial);
  // everyone with access sees a comment, unless it is marked SWI only
  const [vis, setVis] = useState<'internal' | 'shared'>('shared');
  const [over, setOver] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState<string | null>(null);
  const [pick, setPick] = useState(0);
  const ta = useRef<HTMLTextAreaElement>(null);
  const fileIn = useRef<HTMLInputElement>(null);

  const matches = useMemo(
    () =>
      query === null
        ? []
        : people.filter((p) => nameOf(p).toLowerCase().includes(query.toLowerCase()) || (p.email ?? '').toLowerCase().startsWith(query.toLowerCase())).slice(0, 6),
    [query, people],
  );

  function onChange(v: string) {
    setText(v);
    const pos = ta.current?.selectionStart ?? v.length;
    const m = /(^|\s)@([\w.\-' ]{0,30})$/.exec(v.slice(0, pos));
    setQuery(m ? m[2] : null);
    setPick(0);
  }
  function choose(p: Person) {
    const el = ta.current;
    const pos = el?.selectionStart ?? text.length;
    const before = text.slice(0, pos).replace(/@([\w.\-' ]{0,30})$/, `@${nameOf(p)} `);
    const next = before + text.slice(pos);
    setText(next);
    setQuery(null);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(before.length, before.length);
    });
  }
  async function send() {
    const body = text.trim();
    if (!body) return;
    const mentions = people.filter((p) => body.includes(`@${nameOf(p)}`)).map((p) => p.id);
    setBusy(true);
    try {
      await onSend(body, mentions, vis, files);
      setText('');
      setFiles([]);
      setVis('shared');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className={`composer ${over ? 'over' : ''} ${vis === 'internal' ? 'swi-only' : ''}`}
      onDragOver={allowFiles ? (e) => (e.preventDefault(), setOver(true)) : undefined}
      onDragLeave={allowFiles ? () => setOver(false) : undefined}
      onDrop={
        allowFiles
          ? (e) => {
              e.preventDefault();
              setOver(false);
              setFiles((f) => [...f, ...Array.from(e.dataTransfer.files)]);
            }
          : undefined
      }
    >
      <textarea
        ref={ta}
        value={text}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (matches.length) {
            if (e.key === 'ArrowDown') (e.preventDefault(), setPick((p) => (p + 1) % matches.length));
            else if (e.key === 'ArrowUp') (e.preventDefault(), setPick((p) => (p - 1 + matches.length) % matches.length));
            else if (e.key === 'Enter' || e.key === 'Tab') (e.preventDefault(), choose(matches[pick]));
            else if (e.key === 'Escape') setQuery(null);
          } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void send();
        }}
      />
      {matches.length ? (
        <div className="suggest" role="listbox" aria-label="Mention someone">
          {matches.map((p, i) => (
            <button key={p.id} type="button" role="option" aria-selected={i === pick} className={i === pick ? 'on' : ''} onMouseDown={(e) => (e.preventDefault(), choose(p))}>
              <strong>{nameOf(p)}</strong> <span className="muted small">{p.email}</span>
            </button>
          ))}
        </div>
      ) : null}
      {files.length ? (
        <div className="small muted">
          Attached: {files.map((f) => f.name).join(', ')}{' '}
          <button type="button" className="linkbtn small" onClick={() => setFiles([])}>
            clear
          </button>
        </div>
      ) : allowFiles ? (
        <div className="drop-hint">Drop files here or attach</div>
      ) : null}
      <div className="composer-row">
        <div className="toolbar">
          {allowFiles ? (
            <>
              <input ref={fileIn} type="file" multiple hidden onChange={(e) => setFiles([...files, ...Array.from(e.target.files ?? [])])} />
              <Button kind="ghost" onClick={() => fileIn.current?.click()}>
                + Attach
              </Button>
            </>
          ) : null}
          {internal && allowVisibility ? (
            <label className="check small swi-tick" title="Only SWI staff will see this comment">
              <input type="checkbox" checked={vis === 'internal'} onChange={(e) => setVis(e.target.checked ? 'internal' : 'shared')} /> <span className="lock">SWI only</span>
            </label>
          ) : null}
        </div>
        <div className="toolbar">
          {onCancel ? <Button onClick={onCancel}>Cancel</Button> : null}
          <Button kind="primary" onClick={() => void send()} disabled={busy || !text.trim()}>
            {busy ? 'Posting…' : sendLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function Comments({
  itemId,
  siteId,
  canComment,
  files,
  onFilesChanged,
  onCount,
}: {
  itemId: string;
  siteId: string;
  canComment: boolean;
  files: ItemFile[];
  onFilesChanged: () => void;
  onCount?: (n: number) => void;
}) {
  const { session, access, can } = useApp();
  const toast = useToast();
  const viewer = useViewer();
  const me = session?.user.id;
  const internal = Boolean(access?.super_admin || access?.roles.some((r) => INTERNAL_ROLES.has(r.role)));
  const isAdmin = Boolean(access?.super_admin);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  const list = useRows<Comment>(
    () =>
      supabase
        .from('comments')
        .select('id, parent_id, body, mentions, mention_emails, author_id, author_name, author_email, posted_at, edited_at, visibility, ss_comment_id, updated_at, deleted_at')
        .eq('linked_table', 'items')
        .eq('linked_id', itemId)
        .is('deleted_at', null)
        .order('posted_at'),
    [itemId],
    ['comments'],
    `linked_id=eq.${itemId}`,
  );
  const people = useRows<Person>(
    () => supabase.from('profiles').select('id, full_name, email').is('deleted_at', null).order('full_name'),
    [],
    [],
  );

  // oldest first, so the newest sits right above the comment box (like a chat)
  const roots = list.rows.filter((c) => !c.parent_id).sort((a, b) => a.posted_at.localeCompare(b.posted_at));
  useEffect(() => onCount?.(list.rows.length), [list.rows.length, onCount]);
  const repliesOf = (id: string) => list.rows.filter((c) => c.parent_id === id);
  const filesOf = (id: string) => files.filter((f) => f.comment_id === id && !f.deleted_at);

  async function post(body: string, mentions: string[], visibility: 'internal' | 'shared', attach: File[], parent: string | null) {
    try {
      const row = await write<{ id: string }>('comments', 'insert', {
        values: { linked_id: itemId, parent_id: parent, body, mentions, visibility },
        summary: parent ? 'Replied to a comment' : 'Added a comment',
      });
      for (const f of attach) {
        try {
          await uploadFile(f, { siteId, linkedTable: 'items', linkedId: itemId, commentId: row.id });
        } catch (e) {
          toast({ tone: 'error', text: `${f.name}: ${errText(e)}` });
        }
      }
      if (attach.length) onFilesChanged();
      setReplyTo(null);
      void list.reload();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
      throw e;
    }
  }

  async function save(c: Comment, body: string, mentions: string[]) {
    try {
      await write('comments', 'update', { id: c.id, values: { body, mentions, _expect_updated_at: c.updated_at }, summary: 'Edited a comment' });
      setEditing(null);
      void list.reload();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
      throw e;
    }
  }

  async function remove(c: Comment) {
    if (!window.confirm('Delete this comment? It can be restored from the audit log.')) return;
    try {
      await write('comments', 'delete', { id: c.id, summary: 'Deleted a comment' });
      void list.reload();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }

  const one = (c: Comment, isReply: boolean) => {
    const name = c.author_name || c.author_email || 'Someone';
    const mine = c.author_id && c.author_id === me;
    const att = filesOf(c.id);
    return (
      <div className="cmt" key={c.id}>
        <span className="face" aria-hidden="true">
          {initials(name)}
        </span>
        <div className="cmt-body">
          <div className="cmt-head">
            <strong>{name}</strong>
            <span className="muted">{fmtDateTime(c.posted_at)}</span>
            {c.edited_at ? <span className="muted">· edited</span> : null}
            {c.ss_comment_id ? <span className="muted">· from Smartsheet</span> : null}
            {c.visibility === 'internal' ? <span className="lock">SWI only</span> : null}
          </div>
          {editing === c.id ? (
            <Composer
              people={people.rows}
              internal={internal}
              placeholder="Edit comment"
              initial={c.body}
              allowFiles={false}
              allowVisibility={false}
              sendLabel="Save"
              onCancel={() => setEditing(null)}
              onSend={(b, m) => save(c, b, m)}
            />
          ) : (
            <Body text={c.body} people={people.rows} />
          )}
          {att.length ? (
            <div className="cmt-actions">
              {att.map((f) => (
                <button key={f.id} type="button" className="linkbtn" onClick={() => viewer({ id: f.id, name: f.original_name, mime: f.mime, kind: f.kind }, att.map((x) => ({ id: x.id, name: x.original_name, mime: x.mime, kind: x.kind })))}>
                  📎 {f.original_name}
                </button>
              ))}
            </div>
          ) : null}
          {editing !== c.id ? (
            <div className="cmt-actions">
              {canComment && !isReply ? (
                <button type="button" className="linkbtn" onClick={() => setReplyTo(replyTo === c.id ? null : c.id)}>
                  Reply
                </button>
              ) : null}
              {mine ? (
                <button type="button" className="linkbtn" onClick={() => setEditing(c.id)}>
                  Edit
                </button>
              ) : null}
              {mine || isAdmin ? (
                <button type="button" className="linkbtn danger" onClick={() => void remove(c)}>
                  Delete
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="stack" style={{ gap: 12 }}>
      {list.error ? <div className="error-box">{list.error}</div> : null}
      {list.loading ? (
        <Empty>Loading comments…</Empty>
      ) : roots.length === 0 ? (
        <Empty>No comments yet.</Empty>
      ) : (
        <div>
          {roots.map((c) => (
            <div className={`thread ${c.visibility === 'internal' ? 'internal' : ''}`} key={c.id}>
              {one(c, false)}
              {repliesOf(c.id).length || replyTo === c.id ? (
                <div className="replies">
                  {repliesOf(c.id).map((r) => one(r, true))}
                  {replyTo === c.id ? (
                    <Composer
                      people={people.rows}
                      internal={internal}
                      placeholder="Reply — type @ to mention someone"
                      sendLabel="Reply"
                      onCancel={() => setReplyTo(null)}
                      onSend={(b, m, v, f) => post(b, m, v, f, c.id)}
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
      {canComment ? (
        <Composer
          people={people.rows}
          internal={internal}
          placeholder="Comment — type @ to mention"
          onSend={(b, m, v, f) => post(b, m, v, f, null)}
        />
      ) : null}
      {!canComment && can('files', 1) ? <p className="muted small">You can read comments here; your role cannot add them.</p> : null}
    </div>
  );
}
